import { useQuery } from "@tanstack/react-query";
import {
  OrdersClient,
  ProductDetailDrawerDto,
  ProductDetailDrawerRequestDto,
} from "@lib/ShiOneClient";
import { useApi } from "@utils/useApi";
import { useClaims } from "../../../../../auth/ClaimsProvider";
import { ShiOnePermission } from "../../../../../auth/authorizationTypes";
import ms from "ms";
import { OrdersQueryKeys } from "@features/orders-features/hooks/OrdersQueryKeys";
import { isSupportedOrderProductClass } from "@features/orders-features/functions/productQuickView";

export type OrderProductDetailDrawerResponse = ProductDetailDrawerDto;

export interface OrderProductDetailDrawerHookResult {
  data: OrderProductDetailDrawerResponse | null;
  isLoading: boolean;
  isInitialLoading: boolean;
  isError: boolean;
  isFetching: boolean;
  isFetched: boolean;
}

const toNumber = (value?: string | null): number | undefined =>
  value != null ? Number(value) : undefined;

export default function useGetOrderProductDetailDrawer(
  request: ProductDetailDrawerRequestDto | null,
): OrderProductDetailDrawerHookResult {
  const api = useApi(OrdersClient);
  const { hasPermission, getClaimValue } = useClaims();

  const canSearchOrders =
    hasPermission(ShiOnePermission.orderSearchMy) ||
    hasPermission(ShiOnePermission.orderSearchOrgUnit);

  const orgUnitIdCurrent = toNumber(getClaimValue("orgUnitIdCurrent"));

  const isSupportedProductClass = isSupportedOrderProductClass(
    request?.productClass,
  );

  const isEnabled =
    canSearchOrders &&
    request !== null &&
    request.productDescriptor != null &&
    isSupportedProductClass;

  const fetchProductDetailDrawer =
    async (): Promise<ProductDetailDrawerDto | null> => {
      if (!request) return null;
      return await api.getOrderProductDetailDrawer(request);
    };

  const productKey = {
    productDescriptor: request?.productDescriptor,
    productClass: request?.productClass,
    orgunitId: request?.orgunitId ?? orgUnitIdCurrent,
  };

  const query = useQuery({
    queryKey: [OrdersQueryKeys.OrderProductDetailsDrawer, productKey],
    queryFn: fetchProductDetailDrawer,
    enabled: isEnabled,
    retry: false,
    staleTime: ms("1m"),
  });

  return {
    data: query.data ?? null,
    isLoading: query.isLoading,
    // React Query v4 isInitialLoading is false for a first fetch paused offline. Keep that
    // pending, so a routed drawer does not read the missing data as an unknown product.
    isInitialLoading: isEnabled && query.isLoading,
    isError: query.isError,
    isFetching: query.isFetching,
    isFetched: query.isFetched,
  };
}
